import { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";
import type { SubscriptionRow, SubscriptionStatus } from "../types";
import { subscriptionRowToSubscription } from "../types";
import type { Subscription } from "../types";

export interface UseSubscriptionResult {
  subscription: Subscription | null;
  status: SubscriptionStatus | null;
  trialEndsAt: string | null;
  isAllowed: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Charge l'abonnement du tenant (owner_id) et détermine si l'accès est autorisé.
 * isAllowed = true si status in ('trialing', 'active') et (si trialing alors trial_ends_at > now()).
 */
export function useSubscription(ownerId: string | undefined): UseSubscriptionResult {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!ownerId) {
      setSubscription(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("owner_id", ownerId)
        .maybeSingle();
      if (e) {
        setError(e.message);
        setSubscription(null);
        return;
      }
      if (data) {
        setSubscription(subscriptionRowToSubscription(data as SubscriptionRow));
      } else {
        setSubscription(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur chargement abonnement");
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const status = subscription?.status ?? null;
  const trialEndsAt = subscription?.trialEndsAt ?? null;

  const isAllowed =
    status === "trialing"
      ? trialEndsAt != null && new Date(trialEndsAt) > new Date()
      : status === "active";

  return {
    subscription,
    status,
    trialEndsAt,
    isAllowed: Boolean(isAllowed),
    loading,
    error,
    refetch: fetchSubscription,
  };
}
